import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const { signIn, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  // Clear errors when user starts typing
  useEffect(() => {
    if (authError || localError) {
      clearError?.();
      setLocalError('');
    }
  }, [email, password]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLocalError('');
    clearError?.();
    
    if (!email || !password) {
      setLocalError('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);

    try {
      const { user } = await signIn(email, password);
      
      // Small delay to ensure profile is loaded
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get fresh profile data
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', user?.id)
        ?.single();

      // Role-based redirect
      if (profile?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/projeto');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLocalError(error?.message || 'Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const displayError = authError || localError;

  return (
    <>
      {/* Error Alert */}
      {displayError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-fadeIn">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800 font-medium">{displayError}</p>
            {displayError?.includes('conexão') && (
              <p className="text-xs text-red-600 mt-1">
                O servidor está temporariamente indisponível. Tentando reconectar...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
              placeholder="seu@email.com"
              disabled={loading}
              required
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Senha
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
              placeholder="••••••••"
              disabled={loading}
              required
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Entrando...</span>
            </>
          ) : (
            'Entrar'
          )}
        </button>
      </form>
    </>
  );
}